// مشترک صفحات ACC (AP-ACC-01): شناسه صفحات، مسیرها، کلیدهای Query، رنگ وضعیت‌ها و نمایش مبلغ ریالی (ADR-013).
import type {
  AccBankStatementLineStatus,
  AccDailyControlView,
  AccExportBatchStatus,
  AccFiscalPeriodStatus,
  AccJournalExportStatus,
  AccJournalStatus,
  AccMoney,
  AccSourceEntryStatus,
} from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { Money, StatusChip, type StatusTone } from '@phoenix/ui';

/** کدینگ و تفصیلی‌ها (US-ACC-001، 002). */
export const SCR_STAFF_880 = 'SCR-STAFF-880';
/** نگاشت نقش‌های حساب (US-ACC-003، 004). */
export const SCR_STAFF_881 = 'SCR-STAFF-881';
/** اسناد و Drill-down (US-ACC-010 تا 014). */
export const SCR_STAFF_882 = 'SCR-STAFF-882';
/** صف خطای ثبت (US-ACC-013). */
export const SCR_STAFF_883 = 'SCR-STAFF-883';
/** ثبت هزینه (US-ACC-020؛ AP-ACC-02). */
export const SCR_STAFF_884 = 'SCR-STAFF-884';
/** الگو و اسناد تکرارشونده (US-ACC-022؛ AP-ACC-03). */
export const SCR_STAFF_885 = 'SCR-STAFF-885';
/** دوره‌های مالی، مانده افتتاحیه و بستن سال (US-ACC-030 تا 033). */
export const SCR_STAFF_886 = 'SCR-STAFF-886';
/** خروجی‌ها (US-ACC-040 تا 042). */
export const SCR_STAFF_887 = 'SCR-STAFF-887';
/** بانک‌ها و چک‌ها (US-ACC-050، 052). */
export const SCR_STAFF_888 = 'SCR-STAFF-888';
/** گزارش‌های پایه (US-ACC-060). */
export const SCR_STAFF_889 = 'SCR-STAFF-889';
/** داشبورد کنترل روزانه (US-ACC-080). */
export const SCR_STAFF_891 = 'SCR-STAFF-891';

export const ACC_PREFIX = '/accounting';
export const ACC_JOURNALS_PATH = `${ACC_PREFIX}/journals`;
export const journalPath = (id: string) => `${ACC_JOURNALS_PATH}/${id}`;

export const accKeys = {
  all: (tenantId?: string) => ['accounting', tenantId] as const,
  structure: (tenantId?: string) => ['accounting', tenantId, 'structure'] as const,
  accounts: (tenantId?: string) => ['accounting', tenantId, 'accounts'] as const,
  details: (tenantId?: string) => ['accounting', tenantId, 'details'] as const,
  templates: (tenantId?: string) => ['accounting', tenantId, 'templates'] as const,
  roles: (tenantId?: string) => ['accounting', tenantId, 'roles'] as const,
  settings: (tenantId?: string) => ['accounting', tenantId, 'settings'] as const,
  journals: (tenantId: string | undefined, filter: string) =>
    ['accounting', tenantId, 'journals', filter] as const,
  journal: (tenantId: string | undefined, id: string) => ['accounting', tenantId, 'journal', id] as const,
  sources: (tenantId: string | undefined, id: string, lineId: string) =>
    ['accounting', tenantId, 'sources', id, lineId] as const,
  errors: (tenantId: string | undefined, filter: string) =>
    ['accounting', tenantId, 'errors', filter] as const,
  fiscal: (tenantId?: string) => ['accounting', tenantId, 'fiscal'] as const,
  exports: (tenantId?: string) => ['accounting', tenantId, 'exports'] as const,
  banks: (tenantId?: string) => ['accounting', tenantId, 'banks'] as const,
  report: (tenantId: string | undefined, filter: string) =>
    ['accounting', tenantId, 'report', filter] as const,
  control: (tenantId: string | undefined, filter: string) =>
    ['accounting', tenantId, 'control', filter] as const,
  expenses: (tenantId: string | undefined, filter: string) =>
    ['accounting', tenantId, 'expenses', filter] as const,
  expenseOptions: (tenantId?: string) => ['accounting', tenantId, 'expense-options'] as const,
  expenseCategories: (tenantId?: string) => ['accounting', tenantId, 'expense-categories'] as const,
  profiles: (tenantId?: string) => ['accounting', tenantId, 'export-profiles'] as const,
  schedules: (tenantId?: string) => ['accounting', tenantId, 'export-schedules'] as const,
  reconciliation: (tenantId: string | undefined, filter: string) =>
    ['accounting', tenantId, 'reconciliation', filter] as const,
  cheques: (tenantId: string | undefined, filter: string) =>
    ['accounting', tenantId, 'cheques', filter] as const,
  // AP-ACC-03 (دفتر جامع).
  manual: (tenantId: string | undefined, filter: string) =>
    ['accounting', tenantId, 'manual-journals', filter] as const,
  manualJournal: (tenantId: string | undefined, id: string) =>
    ['accounting', tenantId, 'manual-journal', id] as const,
  journalTemplates: (tenantId?: string) => ['accounting', tenantId, 'journal-templates'] as const,
  readiness: (tenantId: string | undefined, year: number) =>
    ['accounting', tenantId, 'close-readiness', year] as const,
  statements: (tenantId: string | undefined, filter: string) =>
    ['accounting', tenantId, 'bank-statements', filter] as const,
  statement: (tenantId: string | undefined, id: string) =>
    ['accounting', tenantId, 'bank-statement', id] as const,
  trialBalance: (tenantId: string | undefined, filter: string) =>
    ['accounting', tenantId, 'trial-balance', filter] as const,
  financials: (tenantId: string | undefined, filter: string) =>
    ['accounting', tenantId, 'financial-statements', filter] as const,
  ledger: (tenantId: string | undefined, filter: string) =>
    ['accounting', tenantId, 'account-ledger', filter] as const,
};

/** عدد از قرارداد (int64 ممکن است رشته باشد). */
export function num(value: number | string | null | undefined): number {
  if (value === null || value === undefined) {
    return 0;
  }
  return typeof value === 'number' ? value : Number(value);
}

/** مبلغ ریالی قرارداد. */
export function Amount({ money, testId }: { money: AccMoney | null | undefined; testId?: string }) {
  if (!money) {
    return <span>—</span>;
  }
  return (
    <span data-testid={testId} data-amount={num(money.amount)}>
      <Money amountMinor={money.amount} currency={money.currency} />
    </span>
  );
}

/** مبلغ ریالی خام (کنترل روزانه). */
export function Rial({ value, testId }: { value: number | string; testId?: string }) {
  return (
    <span data-testid={testId} data-amount={num(value)}>
      <Money amountMinor={value} currency="IRR" />
    </span>
  );
}

function chip(testId: string, status: string, tone: StatusTone, labelKey: string) {
  return (
    <span data-testid={testId} data-status={status}>
      <StatusChip tone={tone} labelKey={labelKey} />
    </span>
  );
}

export function EntryStatusChip({ status }: { status: AccSourceEntryStatus }) {
  const tone: StatusTone = status === 'Aggregated' ? 'done' : status === 'Pending' ? 'pending' : 'no_show';
  return chip('acc-entry-status', status, tone, `acc:entry_status.${status}`);
}

export function JournalExportChip({ status }: { status: AccJournalExportStatus }) {
  const tone: StatusTone = status === 'Exported' ? 'done' : status === 'Failed' ? 'no_show' : 'inactive';
  return chip('acc-journal-export', status, tone, `acc:export_status.${status}`);
}

export function BatchStatusChip({ status }: { status: AccExportBatchStatus }) {
  const tone: StatusTone =
    status === 'Confirmed'
      ? 'done'
      : status === 'Failed'
        ? 'no_show'
        : status === 'Generated'
          ? 'pending'
          : 'in_service';
  return chip('acc-batch-status', status, tone, `acc:batch_status.${status}`);
}

export function PeriodStatusChip({ status }: { status: AccFiscalPeriodStatus }) {
  const tone: StatusTone = status === 'Open' ? 'confirmed' : status === 'Locked' ? 'pending' : 'inactive';
  return chip('acc-period-status', status, tone, `acc:period_status.${status}`);
}

/** وضعیت سند دستی در گردش تأیید (AP-ACC-03؛ Draft، PendingApproval، Rejected، Posted، Reversed). */
export function JournalStatusChip({ status }: { status: AccJournalStatus }) {
  const tone: StatusTone =
    status === 'Posted'
      ? 'done'
      : status === 'PendingApproval'
        ? 'pending'
        : status === 'Rejected'
          ? 'no_show'
          : status === 'Reversed'
            ? 'cancelled'
            : 'inactive';
  return chip('acc-journal-status', status, tone, `acc:journal_status.${status}`);
}

/** وضعیت تطبیق ردیف صورت‌حساب بانک (AP-ACC-03). */
export function BankLineStatusChip({ status }: { status: AccBankStatementLineStatus }) {
  const tone: StatusTone =
    status === 'Matched'
      ? 'done'
      : status === 'Resolved'
        ? 'confirmed'
        : status === 'NeedsEntry'
          ? 'pending'
          : 'inactive';
  return chip('acc-bank-line-status', status, tone, `acc:statement.line_status.${status}`);
}

export function ControlChip({ row }: { row: AccDailyControlView }) {
  return chip(
    'acc-control-status',
    row.isOk ? 'ok' : 'mismatch',
    row.isOk ? 'done' : 'no_show',
    row.isOk ? 'acc:control.ok' : 'acc:control.mismatch',
  );
}

/** ذخیره Blob با نام فایل. */
export function saveBlob(blob: Blob, fileName: string) {
  if (typeof URL.createObjectURL !== 'function') {
    return;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** فایل ← Base64 (ورود کدینگ). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('read'));
    reader.readAsDataURL(file);
  });
}

/** شعبه‌های مستأجر جاری (فیلتر و دامنه نگاشت). */
export function useAccBranches() {
  const { activeTenant } = useTenantSession();
  return activeTenant?.branches ?? [];
}

/** نام شعبه از شناسه. */
export function useBranchName() {
  const branches = useAccBranches();
  return (id: string | null | undefined) =>
    id ? (branches.find((branch) => branch.branchId === id)?.name ?? id.slice(0, 8)) : null;
}

/** ویرگول فارسی (جداکننده نقش‌های ردیف سند؛ هم‌ارز JournalEntry.Post). */
export const LIST_SEPARATOR = `${String.fromCharCode(0x060c)} `;
